// 极简全局状态存储：单一可变对象，不含订阅/观察机制
// Minimal global state store: single mutable object, no pub-sub

import type { AuthStatus } from "@/api/auth";
import type {
  ArtifactMeta,
  DemoABEntry,
  DemoSingleEntry,
  Mode,
  PlanRef,
  ReportStatus,
  SimulationRow,
  TaskListItem,
  TimelineItem,
  WorkbenchView,
} from "@/lib/types";

// 演示目录集合 / Demo catalog collection shape.
export interface DemoCollections {
  single: DemoSingleEntry[];
  ab: DemoABEntry[];
  reports: Array<{ id: string; title: string }>;
}

// 当前选中的演示 id 集合 / Currently-selected demo ids by kind.
export interface SelectedDemos {
  single: string | null;
  ab: string | null;
  report: string | null;
}

// Markdown 上传插槽内容 / Markdown upload buffer per plan slot.
export interface MarkdownSlot {
  filename: string;
  content: string;
}

// 当前任务上下文 / Current task context.
export type CurrentTask =
  | { kind: "demo"; mode: Mode; demoId: string; reportSlug: string | null }
  | { kind: "history"; simulationId: string; reportId: string | null; reportStatus: ReportStatus }
  | { kind: "draft" };

// 报告生成中的全局状态 / Global in-flight report generation state.
export interface ReportGenerationState {
  simulationId: string;
  taskId?: string | null;
  jobId?: string | null;
  status: "pending";
}

export interface AppState {
  mode: Mode;
  workbenchView: WorkbenchView;
  tasks: TaskListItem[];
  selectedTaskId: string | null;
  auth: AuthStatus | null;
  planA: PlanRef | null;
  planB: PlanRef | null;
  activeSimulation: SimulationRow | null;
  lastReportId: string | null;
  currentTask: CurrentTask | null;
  taskTitle: string;
  history: SimulationRow[];
  timeline: TimelineItem[];
  artifacts: Record<string, ArtifactMeta>;
  // 以下字段在 Task 10 中补齐 / Added in Task 10:
  demos: DemoCollections;
  selectedDemo: SelectedDemos;
  markdown: { A: MarkdownSlot; B: MarkdownSlot };
  running: boolean;
  reportGeneration: ReportGenerationState | null;
}

export const state: AppState = {
  mode: "single",
  workbenchView: "demo",
  tasks: [],
  selectedTaskId: null,
  auth: null,
  planA: null,
  planB: null,
  activeSimulation: null,
  lastReportId: null,
  currentTask: null,
  taskTitle: "",
  history: [],
  timeline: [],
  artifacts: {},
  demos: { single: [], ab: [], reports: [] },
  selectedDemo: { single: null, ab: null, report: null },
  markdown: {
    A: { filename: "plan-a.md", content: "" },
    B: { filename: "plan-b.md", content: "" },
  },
  running: false,
  reportGeneration: null,
};

/**
 * 重置为初始状态 / Reset the store to initial values.
 * 测试隔离时会在 beforeEach 中调用。
 * Called in test beforeEach hooks to prevent state bleed.
 */
export function reset(): void {
  state.mode = "single";
  state.workbenchView = "demo";
  state.tasks = [];
  state.selectedTaskId = null;
  state.auth = null;
  state.planA = null;
  state.planB = null;
  state.activeSimulation = null;
  state.lastReportId = null;
  state.currentTask = null;
  state.taskTitle = "";
  state.history = [];
  state.timeline = [];
  state.artifacts = {};
  state.demos = { single: [], ab: [], reports: [] };
  state.selectedDemo = { single: null, ab: null, report: null };
  state.markdown = {
    A: { filename: "plan-a.md", content: "" },
    B: { filename: "plan-b.md", content: "" },
  };
  state.running = false;
  state.reportGeneration = null;
}
